import React, { useMemo } from 'react';
import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { ColourChip } from '../../../components/general/mini_components-ts';
import LoadingState from '../AnalyticsBeta/common/LoadingState';
import ErrorStateView from '../AnalyticsBeta/common/ErrorState';
import { QcVisitCode, STATE_PALETTE, buildVisitCode, stateTranslateId } from './visitQueueRow';
import type { CorrectionFlagState } from './correctionFlag';

/** Derived server-side from whether the visit carries QC feedback yet — never stored. */
export type QcQueueState = 'submitted' | 'in_review' | 'approved';

export type QcQueueStateFilter = QcQueueState | 'all';

/**
 * SKIN-441 — the second, independent filter axis: visits carrying at least one lesion
 * nobody has diagnosed.
 *
 * Its own type rather than another member of QcQueueStateFilter, because the two are
 * orthogonal: a visit is in exactly one review state and may separately have a
 * diagnosis gap, so folding them into one control would make "in review" and "has a
 * gap" mutually exclusive on screen when they are not.
 */
export type QcQueueDiagnosisFilter = 'all' | 'undiagnosed';

/**
 * SKIN-577 — the third axis: visits whose correction flag is open (OPEN or CORRECTED). Its
 * own type for the reason the diagnosis axis has one — it is independent of the other two.
 */
export type QcQueueCorrectionFilter = 'all' | 'needs_correction';

// SKIN-239 — one row of the QC review queue. Mirrors the API's IQcQueueRow: opaque
// identifiers plus batch-level counts. There is deliberately no quality score here —
// QC lives on the image, not the visit (epic SKIN-230).
export interface QcQueueRow {
    visitUuid: string;
    visitId: number;
    visitCode: QcVisitCode;
    state: QcQueueState;
    submittedAt: string | null;
    dermatologist: { uuid: string; name: string; surnames: string } | null;
    imageCount: number;
    requiredImageSetIncomplete: boolean;
    /**
     * SKIN-441 — the visit's lesions on its canonical submission, and how many carry no
     * diagnosis. A count rather than a flag: a reviewer triages "3 of 7" differently
     * from "1 of 7", and a boolean renders both the same.
     */
    lesionCount: number;
    undiagnosedLesionCount: number;
    /**
     * SKIN-577 — the visit's correction flag while it is open (OPEN: awaiting the correction;
     * CORRECTED: awaiting the reviewer), null when there is none or it was resolved.
     */
    openCorrectionFlag: { state: Exclude<CorrectionFlagState, 'RESOLVED'>; assigneeUuid: string } | null;
}

interface VisitQueueProps extends LocalizeContextProps {
    loading: boolean;
    error?: Error | null;
    rows: QcQueueRow[];
    stateFilter: QcQueueStateFilter;
    onStateFilterChange: (state: QcQueueStateFilter) => void;
    diagnosisFilter: QcQueueDiagnosisFilter;
    onDiagnosisFilterChange: (filter: QcQueueDiagnosisFilter) => void;
    onRetry?: () => void;
    /** Wired to the visit review screen by SKIN-240; the queue itself only reports the click. */
    onSelectVisit?: (visitUuid: string) => void;
    /**
     * SKIN-577 — the correction axis, present only where the study allows correction flags
     * (`WEB_CLINICAL_CORRECTION`). Absent, the queue shows neither its filter nor its chip.
     */
    correction?: {
        filter: QcQueueCorrectionFilter;
        onFilterChange: (filter: QcQueueCorrectionFilter) => void;
    };
}

const STATE_FILTERS: QcQueueStateFilter[] = ['all', 'submitted', 'in_review', 'approved'];
const DIAGNOSIS_FILTERS: QcQueueDiagnosisFilter[] = ['all', 'undiagnosed'];
const CORRECTION_FILTERS: QcQueueCorrectionFilter[] = ['all', 'needs_correction'];

const correctionFilterTranslateId = (filter: QcQueueCorrectionFilter): string =>
    `hospital.quality-check.queue.correction-filter.${filter}`;

const diagnosisFilterTranslateId = (filter: QcQueueDiagnosisFilter): string =>
    `hospital.quality-check.queue.diagnosis-filter.${filter}`;

const translateId = stateTranslateId;

const formatDate = (iso: string | null): React.ReactNode => {
    if (!iso) return <Translate id="hospital.quality-check.queue.not-submitted" />;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

/**
 * One filter select. Two axes narrow this queue and a third is already sketched
 * (SKIN-442), so the control is written once and given its values.
 *
 * Generic in the option type rather than taking `string[]`: it is what keeps
 * `onChange` typed as the caller's own union, so a state-filter handler cannot be
 * wired to the diagnosis options by mistake.
 */
interface QueueFilterProps<T extends string> {
    testId: string;
    labelId: string;
    value: T;
    options: readonly T[];
    optionLabelId: (option: T) => string;
    onChange: (next: T) => void;
    translate: LocalizeContextProps['translate'];
    width?: number;
}

function QueueFilter<T extends string>({
    testId, labelId, value, options, optionLabelId, onChange, translate, width = 260,
}: QueueFilterProps<T>) {
    return (
        <Box sx={{ width }}>
            <TextField
                select
                fullWidth
                size="small"
                SelectProps={{ native: true }}
                label={<Translate id={labelId} />}
                value={value}
                onChange={(e) => onChange(e.target.value as T)}
                inputProps={{ 'data-testid': testId }}
            >
                {/* A native <option> renders text, not React, so <Translate> would
                    stringify to "[object Object]" here. Only the option labels need the
                    translate function; the rest of the screen stays declarative. */}
                {options.map(option => (
                    <option key={option} value={option}>
                        {String(translate(optionLabelId(option)))}
                    </option>
                ))}
            </TextField>
        </Box>
    );
}

/**
 * A right-aligned numeric cell that may carry a warning beside it.
 *
 * Both count columns are this shape — a figure, and an icon when the figure is a
 * problem — so the shape is written once. Two copies drifted apart the moment the
 * second one needed a different tooltip, which is how a table ends up with two
 * warnings that look subtly unalike.
 */
function CountCell({ warn, warningId, warningTestId, children }: {
    warn: boolean;
    warningId: string;
    warningTestId: string;
    children: React.ReactNode;
}) {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <span>{children}</span>
            {warn && (
                <Tooltip title={<Translate id={warningId} />}>
                    <WarningAmberIcon data-testid={warningTestId} fontSize="small" color="warning" />
                </Tooltip>
            )}
        </Stack>
    );
}

const VisitQueue: React.FC<VisitQueueProps> = ({
    loading, error, rows, stateFilter, onStateFilterChange,
    diagnosisFilter, onDiagnosisFilterChange, onRetry, onSelectVisit, correction, translate,
}) => {
    const theme = useTheme();

    const headCells = useMemo(() => ([
        { id: 'visitCode', alignment: 'left', label: <Translate id="hospital.quality-check.queue.visit-code" /> },
        { id: 'dermatologist', alignment: 'left', label: <Translate id="hospital.quality-check.queue.dermatologist" /> },
        { id: 'submittedAt', alignment: 'left', label: <Translate id="hospital.quality-check.queue.submitted-at" /> },
        { id: 'images', alignment: 'right', label: <Translate id="hospital.quality-check.queue.images" /> },
        { id: 'lesions', alignment: 'right', label: <Translate id="hospital.quality-check.queue.lesions" /> },
        { id: 'state', alignment: 'left', label: <Translate id="hospital.quality-check.queue.state-column" /> },
    ]), []);

    // The response carries every reviewable visit with its state and its lesion counts
    // on each row, so both filters are a narrowing of what is already here rather than
    // another request. They are ANDed: the axes are independent (see
    // QcQueueDiagnosisFilter), so a reviewer can ask for "submitted AND missing a
    // diagnosis" and that is a real, useful question.
    const correctionFilter = correction?.filter ?? 'all';
    const visible = useMemo(() => rows.filter(r => (
        (stateFilter === 'all' || r.state === stateFilter)
        && (diagnosisFilter === 'all' || r.undiagnosedLesionCount > 0)
        && (correctionFilter === 'all' || r.openCorrectionFlag !== null)
    )), [rows, stateFilter, diagnosisFilter, correctionFilter]);
    const showCorrection = !!correction;

    const tableRows = useMemo(() => visible.map(r => ({
        // EnhancedTable hands this id straight back to selectRow, so the row click
        // already carries the visit uuid — no lookup needed.
        id: r.visitUuid,
        visitCode: buildVisitCode(r.visitCode),
        dermatologist: r.dermatologist
            ? `${r.dermatologist.name} ${r.dermatologist.surnames}`
            : <Translate id="hospital.quality-check.queue.unassigned" />,
        submittedAt: formatDate(r.submittedAt),
        images: (
            <CountCell
                warn={r.requiredImageSetIncomplete}
                warningId="hospital.quality-check.queue.incomplete-set"
                warningTestId="incomplete-set"
            >
                {r.imageCount}
            </CountCell>
        ),
        lesions: (
            /*
             * The bare total when nothing is missing, and the ratio only when something
             * is. "0 of 4 undiagnosed" on every complete visit would make the phrase
             * noise, and the warning icon beside it meaningless.
             */
            <CountCell
                warn={r.undiagnosedLesionCount > 0}
                warningId="hospital.quality-check.queue.undiagnosed-tooltip"
                warningTestId="undiagnosed-warning"
            >
                {r.undiagnosedLesionCount > 0
                    ? (
                        <span data-testid="undiagnosed-lesions">
                            <Translate
                                id="hospital.quality-check.queue.undiagnosed-lesions"
                                data={{ undiagnosed: r.undiagnosedLesionCount, total: r.lesionCount }}
                            />
                        </span>
                    )
                    : r.lesionCount}
            </CountCell>
        ),
        state: (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <ColourChip
                    size="small"
                    rgbcolor={theme.palette[STATE_PALETTE[r.state]].main}
                    label={<Translate id={translateId(r.state)} />}
                />
                {/* SKIN-577 — beside the state, not instead of it: a flagged visit is still submitted. */}
                {showCorrection && r.openCorrectionFlag && (
                    <ColourChip
                        size="small"
                        data-testid="correction-chip"
                        data-state={r.openCorrectionFlag.state}
                        rgbcolor={theme.palette.error.main}
                        label={<Translate id={`hospital.quality-check.queue.correction-state.${r.openCorrectionFlag.state}`} />}
                    />
                )}
            </Stack>
        ),
    })), [visible, theme, showCorrection]);

    const filter = (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <QueueFilter
                testId="state-filter"
                labelId="hospital.quality-check.queue.filter-label"
                value={stateFilter}
                options={STATE_FILTERS}
                optionLabelId={translateId}
                onChange={onStateFilterChange}
                translate={translate}
            />
            <QueueFilter
                testId="diagnosis-filter"
                labelId="hospital.quality-check.queue.diagnosis-filter-label"
                value={diagnosisFilter}
                options={DIAGNOSIS_FILTERS}
                optionLabelId={diagnosisFilterTranslateId}
                onChange={onDiagnosisFilterChange}
                translate={translate}
                // Wider than the state filter beside it: its longest option is one word
                // ("Approved"), while this one's is a phrase that runs to 28 characters
                // in Spanish and French. At the shared 260px "With undiagnosed lesions"
                // rendered as "With undiagnosed lesio…", hiding the only word that says
                // what the filter does.
                width={320}
            />
            {correction && (
                <QueueFilter
                    testId="correction-filter"
                    labelId="hospital.quality-check.queue.correction-filter-label"
                    value={correction.filter}
                    options={CORRECTION_FILTERS}
                    optionLabelId={correctionFilterTranslateId}
                    onChange={correction.onFilterChange}
                    translate={translate}
                />
            )}
            {/* Changing the filter no longer re-fetches, so without this the queue a
                reviewer sits in all morning stays frozen at whatever mount returned.
                `retry` used to be reachable only from the error state. */}
            <Tooltip title={<Translate id="hospital.quality-check.queue.refresh" />}>
                <span>
                    <IconButton
                        data-testid="refresh-queue"
                        aria-label="refresh"
                        size="small"
                        onClick={onRetry}
                        disabled={!onRetry || loading}
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );

    const renderBody = () => {
        if (loading) return <LoadingState />;
        if (error) return <ErrorStateView error={error} onRetry={onRetry} />;

        // Holding every row makes the two empty states observable instead of inferred:
        // nothing fetched at all is a quiet investigation, while a populated queue
        // narrowed to nothing is a filter result. Deriving this from the filter value
        // would tell a reviewer whose investigation has no visits that none "match this
        // filter" — true but misleading, and it sends them hunting through the filter.
        if (!rows.length || !visible.length) {
            const reason = !rows.length ? 'no-visits' : 'no-match';
            return (
                <Box data-testid="empty-state" data-reason={reason} sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="textSecondary">
                        <Translate id={`hospital.quality-check.queue.empty.${reason}`} />
                    </Typography>
                </Box>
            );
        }

        // EnhancedTable indexes rows[0] to infer column types, so it must not be
        // rendered with an empty list — the empty state above is the guard.
        return (
            <EnhancedTable
                noHeader
                noSelectable
                disableOrder
                headCells={headCells}
                rows={tableRows}
                selectRow={onSelectVisit}
            />
        );
    };

    return (
        <Box>
            {filter}
            {renderBody()}
        </Box>
    );
};

// withLocalize injects `translate`; the runtime also exposes a LocalizeContext, but it
// is absent from the package's type definitions, so the HOC is the typed way in — and
// the one the rest of this repo already uses (see components/general/SelectField.tsx).
export default withLocalize(VisitQueue);
